/**
 * Adding one of the firm's own templates: one file (PDF, Word or Excel), a
 * title, what kind of document it starts, and the jurisdictions it is for.
 * The API reads the blanks out of the file once it is stored.
 *
 * @module components/regulation/templates/TemplateUploadDialog
 */
import { POLICY_TEMPLATE_ACCEPT, policyTemplateFileProblem } from "@repo/shared/policyTemplates";
import { FileUpIcon, Loader2Icon, UploadIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";

import { usePolicies } from "../../../state/policies";
import type { PolicyTemplate, PolicyTemplatesView } from "../../../state/policyTemplates";
import { Button } from "../../ui/button";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxValue,
} from "../../ui/combobox";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { Textarea } from "../../ui/textarea";
import { formatFileSize } from "../policyUploadMatch";

/** "CDD Policy TEMPLATE.docx" → "CDD Policy TEMPLATE", as a starting title. */
const titleFromFile = (name: string): string => name.replace(/\.[a-z0-9]{1,5}$/i, "").trim();

export function TemplateUploadDialog({
  organizationId,
  open,
  onOpenChange,
  upload,
  onUploaded,
}: {
  readonly organizationId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly upload: PolicyTemplatesView["upload"];
  readonly onUploaded?: (template: PolicyTemplate) => void;
}) {
  // The organisation's adopted jurisdictions are the only ones a template may name.
  const { snapshot } = usePolicies(organizationId);
  const orgJurisdictions = snapshot?.jurisdictions ?? [];
  const jurisdictionName = useCallback(
    (slug: string) => orgJurisdictions.find((entry) => entry.slug === slug)?.name ?? slug,
    [orgJurisdictions],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [policyType, setPolicyType] = useState("");
  const [description, setDescription] = useState("");
  const [documentKind, setDocumentKind] = useState<"policy" | "procedure">("policy");
  const [jurisdictions, setJurisdictions] = useState<ReadonlyArray<string>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setTitle("");
    setPolicyType("");
    setDescription("");
    setDocumentKind("policy");
    setJurisdictions([]);
    setBusy(false);
    setError(null);
  }, [open]);

  const choose = (picked: File | undefined) => {
    if (!picked) return;
    const problem = policyTemplateFileProblem(picked);
    setError(problem);
    if (problem !== null) return;
    setFile(picked);
    setTitle((current) => (current.trim().length > 0 ? current : titleFromFile(picked.name)));
  };

  const submit = async () => {
    if (!file || title.trim().length === 0) return;
    setBusy(true);
    setError(null);
    const outcome = await upload({
      file,
      title,
      description,
      policyType,
      documentKind,
      jurisdictionSlugs: jurisdictions,
    });
    setBusy(false);
    if (outcome.error !== null) {
      setError(outcome.error);
      return;
    }
    onUploaded?.(outcome.value);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        onOpenChange(next);
      }}
    >
      <DialogPopup className="flex max-h-[85vh] max-w-xl flex-col">
        <DialogHeader>
          <DialogTitle>Add a template</DialogTitle>
          <DialogDescription>
            One of your firm's own templates. eve starts policies from it and fills its blanks from
            your organisation's details; only your organisation sees it.
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <input
            ref={inputRef}
            type="file"
            accept={POLICY_TEMPLATE_ACCEPT}
            className="hidden"
            onChange={(event) => {
              choose(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              choose(event.dataTransfer?.files?.[0]);
            }}
            disabled={busy}
            className={cn(
              "flex w-full cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-8 text-center transition-colors",
              dragging
                ? "border-primary/60 bg-primary/5"
                : "border-border hover:border-foreground/30 hover:bg-muted/40",
            )}
          >
            <FileUpIcon className="size-5 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {file ? file.name : "Drop a template here, or click to choose"}
            </span>
            <span className="text-xs text-muted-foreground">
              {file
                ? formatFileSize(file.size)
                : "PDF, Word (.docx) or Excel (.xlsx) · up to 25 MB"}
            </span>
          </button>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Title</span>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Customer due diligence policy"
              maxLength={200}
              disabled={busy}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Type (optional)</span>
              <Input
                value={policyType}
                onChange={(event) => setPolicyType(event.target.value)}
                placeholder="e.g. AML/CFT policy"
                maxLength={120}
                disabled={busy}
              />
            </label>
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">It starts a</span>
              <Select
                value={documentKind}
                onValueChange={(next) =>
                  setDocumentKind(next === "procedure" ? "procedure" : "policy")
                }
              >
                <SelectTrigger
                  size="sm"
                  className="cursor-pointer"
                  aria-label="What it starts"
                  disabled={busy}
                >
                  {documentKind === "procedure" ? "Procedure" : "Policy"}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="policy">Policy</SelectItem>
                  <SelectItem value="procedure">Procedure</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              What it is for (optional)
            </span>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="e.g. Group template, adapted per entity"
              maxLength={2_000}
              disabled={busy}
            />
          </label>

          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">Jurisdictions</span>
            <Combobox
              multiple
              items={orgJurisdictions.map((jurisdiction) => jurisdiction.slug)}
              value={[...jurisdictions]}
              onValueChange={(next) => setJurisdictions(next)}
              itemToStringLabel={(slug) => jurisdictionName(slug)}
              disabled={busy}
            >
              <ComboboxChips>
                <ComboboxValue>
                  {(value: ReadonlyArray<string>) =>
                    value.map((slug) => (
                      <ComboboxChip key={slug} aria-label={jurisdictionName(slug)}>
                        {jurisdictionName(slug)}
                      </ComboboxChip>
                    ))
                  }
                </ComboboxValue>
                <ComboboxChipsInput
                  placeholder={jurisdictions.length === 0 ? "All of your jurisdictions" : undefined}
                />
              </ComboboxChips>
              <ComboboxPopup>
                <ComboboxEmpty>No matching jurisdiction.</ComboboxEmpty>
                <ComboboxList>
                  {(slug: string) => (
                    <ComboboxItem key={slug} value={slug}>
                      {jurisdictionName(slug)}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxPopup>
            </Combobox>
          </div>

          {error !== null ? <p className="text-sm text-destructive">{error}</p> : null}
        </DialogPanel>

        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="sm" variant="ghost" className="cursor-pointer">
                Cancel
              </Button>
            }
          />
          <Button
            type="button"
            size="sm"
            className="cursor-pointer gap-1.5"
            disabled={busy || file === null || title.trim().length === 0}
            onClick={() => void submit()}
          >
            {busy ? (
              <Loader2Icon className="size-3.5 animate-spin" />
            ) : (
              <UploadIcon className="size-3.5" />
            )}
            {busy ? "Uploading…" : "Add template"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
